import { RSAA } from 'redux-api-middleware';

import { getIdentityState } from '../user/selectors';
import {
  CREATE_ORG_REQUESTED,
  CREATE_ORG_SUCCESS,
  CREATE_ORG_FAILURE,
  CREATE_AWS_ORG_REQUESTED,
  CREATE_AWS_ORG_SUCCESS,
  CREATE_AWS_ORG_FAILURE,
  UPDATE_ORG_REQUESTED,
  UPDATE_ORG_SUCCESS,
  UPDATE_ORG_FAILURE,
  CREATE_STRIPE_SOURCE_REQUESTED,
  CREATE_STRIPE_SOURCE_SUCCESS,
  CREATE_STRIPE_SOURCE_FAILURE,
  UPDATE_STRIPE_SOURCE_REQUESTED,
  UPDATE_STRIPE_SOURCE_SUCCESS,
  UPDATE_STRIPE_SOURCE_FAILURE,
  GET_ORG_REQUESTED,
  GET_ORG_SUCCESS,
  GET_ORG_FAILURE,
  BILLING_SETUP_COMPLETED,
  GET_UPCOMING_INVOICE_REQUESTED,
  GET_UPCOMING_INVOICE_SUCCESS,
  GET_UPCOMING_INVOICE_FAILURE,
  GET_PAST_INVOICES_REQUESTED,
  GET_PAST_INVOICES_SUCCESS,
  GET_PAST_INVOICES_FAILURE,
} from '../actions';

const createOrgActionSet = (data) => ({
  [RSAA]: {
    types: [
      CREATE_ORG_REQUESTED,
      {
        type: CREATE_ORG_SUCCESS,
        meta: {
          analytics: true,
          company: data.companyName,
          phone: data.phoneNumber,
        },
      },
      CREATE_ORG_FAILURE,
    ],
    method: 'POST',
    endpoint: '/organizations/',
    body: JSON.stringify(data),
  },
});

/**
 * Creates an organization based on the provided details
 *
 * @param  {object} details - The details of the org
 */
export const createOrg = (details) => (dispatch) => dispatch(createOrgActionSet(details));

const createAWSOrgActionSet = (awsToken) => ({
  [RSAA]: {
    types: [
      CREATE_AWS_ORG_REQUESTED,
      {
        type: CREATE_AWS_ORG_SUCCESS,
        meta: {
          analytics: true,
        },
      },
      CREATE_AWS_ORG_FAILURE,
    ],
    method: 'POST',
    endpoint: '/awsorg/',
    body: JSON.stringify({
      token: awsToken,
    }),
  },
});

/**
 * Creates an organization based on the provided details
 *
 * @param  {object} details - The details of the org
 */
export const createAWSOrg = (token) => (dispatch) => dispatch(createAWSOrgActionSet(token));

const updateOrgActionSet = (id, data) => ({
  [RSAA]: {
    types: [
      UPDATE_ORG_REQUESTED,
      {
        type: UPDATE_ORG_SUCCESS,
        meta: { analytics: true },
      },
      UPDATE_ORG_FAILURE,
    ],
    method: 'PATCH',
    endpoint: `/organizations/${id}/`,
    body: JSON.stringify(data),
  },
});

/**
 * Updates the existing organization that the logged-in user is associated with
 *
 * @param {object} details - The details of the org to update
 */
export const updateOrg = (data) => (dispatch, getState) =>
  dispatch(updateOrgActionSet(getIdentityState(getState()).organizationId, data));

const getOrgActionSet = (id) => ({
  [RSAA]: {
    types: [GET_ORG_REQUESTED, GET_ORG_SUCCESS, GET_ORG_FAILURE],
    method: 'GET',
    endpoint: `/organizations/${id}/`,
  },
});

/**
 * Loads the organization details for the logged-in user
 */
export const getOrg = () => (dispatch, getState) => {
  const orgId =
    getIdentityState(getState()).impersonatedOrganizationId ||
    getIdentityState(getState()).organizationId;
  return dispatch(getOrgActionSet(orgId));
};

const updateStripeSourceActionSet = (data) => ({
  [RSAA]: {
    types: [
      UPDATE_STRIPE_SOURCE_REQUESTED,
      {
        type: UPDATE_STRIPE_SOURCE_SUCCESS,
        meta: { analytics: true },
      },
      UPDATE_STRIPE_SOURCE_FAILURE,
    ],
    method: 'POST',
    endpoint: '/setstripesource/',
    body: JSON.stringify({ source_id: data }),
  },
});

/**
 * Updates the Stripe source for the user's organization
 *
 * @param {string} sourceId - The Stripe source id of the payment
 */
export const updateStripeSource = (sourceId) => (dispatch) =>
  dispatch(updateStripeSourceActionSet(sourceId));

/**
 * Updates the Stripe source for the user's organization
 *
 * @param {function} stripeCreateFn - A function provided by the Stripe library to create a source
 * @param {object} payment - An object representing the payment type being created
 *
 * Note: Accepts the stripeCreateFn since the Stripe lib functions are injected in components
 */
export const createStripeSource = (stripeCreateFn, payment) => (dispatch, getState) => {
  const userInfo = {
    owner: {
      email: getIdentityState(getState()).email,
    },
  };

  const successHandler = (resolve) => (payload) => {
    if (payload.error) {
      throw payload.error;
    }

    dispatch({
      type: CREATE_STRIPE_SOURCE_SUCCESS,
      meta: {
        analytics: true,
      },
    });

    resolve(payload);
  };

  dispatch({
    type: CREATE_STRIPE_SOURCE_REQUESTED,
  });

  return new Promise((resolve, reject) => {
    try {
      stripeCreateFn({ ...payment, ...userInfo })
        .then(successHandler(resolve))
        .catch((err) => {
          dispatch({
            type: CREATE_STRIPE_SOURCE_FAILURE,
          });
          reject(err);
        });
    } catch (err) {
      dispatch({
        type: CREATE_STRIPE_SOURCE_FAILURE,
      });
      reject(err);
    }
  });
};

/**
 * Updates the billSetupComplete field for the user's organization
 */
export const completeBillingSetup = () => (dispatch) =>
  dispatch({
    type: BILLING_SETUP_COMPLETED,
  });

const getUpcomingInvoiceActionSet = (orgId) => ({
  [RSAA]: {
    types: [
      GET_UPCOMING_INVOICE_REQUESTED,
      GET_UPCOMING_INVOICE_SUCCESS,
      GET_UPCOMING_INVOICE_FAILURE,
    ],
    method: 'GET',
    endpoint: `/invoices/upcoming/?organizationId=${orgId}`,
  },
});

/**
 * Get an organization's upcoming invoice
 *
 */
export const getUpcomingInvoice = (orgId) => (dispatch) =>
  dispatch(getUpcomingInvoiceActionSet(orgId));

const getPastInvoicesActionSet = (orgId) => ({
  [RSAA]: {
    types: [GET_PAST_INVOICES_REQUESTED, GET_PAST_INVOICES_SUCCESS, GET_PAST_INVOICES_FAILURE],
    method: 'GET',
    endpoint: `/invoices/past/?organizationId=${orgId}`,
  },
});

/**
 * Get an organization's upcoming invoice
 *
 */
export const getPastInvoices = (orgId) => (dispatch) => dispatch(getPastInvoicesActionSet(orgId));
